import { combineReducers } from 'redux'
import _ from 'lodash'

const concatPath = _.curryRight(
  (parentPath, relPath) => parentPath ? parentPath.concat('.', relPath) : relPath
)
const concatSelfPath = concatPath('self')

// Get stack for BFS array of paths
const getListOfPathsBFS = (object) => {
  let paths = Object.keys(object).filter(key => key !== 'self')
  let resultPaths = [...paths]
  let path = paths.shift()
  // first go through first depth than next depth
  while (path) {
    let objectAtPath = _.get(object, path)
    let objectKeys = Object.keys(objectAtPath).filter(key => key !== 'self')
    let fullPaths = objectKeys.map(key => concatPath(path, key))
    paths = [...paths, ...fullPaths]
    resultPaths = [...resultPaths, ...fullPaths]

    path = paths.shift()
  }
  return resultPaths
}

// each node will have a self node to set a reducer
// if no self node is set create a reducer that just returns its current state
const reducers = {}
const stores = new WeakMap()

export const createInjectStore = function (store, existingReducer) {
  const {
    registeredReducers,
    combinedReducer,
  } = stores.get(store) || {
    registeredReducers: reducers,
    combinedReducer: s => s,
  }

  stores.set(store, {
    registeredReducers,
    combinedReducer: existingReducer || combinedReducer,
  })

  // injectReducer
  // example: injectReducer('store.path.name', {}, (state, action) => state))
  return (storePathName, initialState, reducer) => {
    // initializes registeredReducers for the given storePathName
    // For each level in the store check if it has a self reducer
    /*storePathName.split('.')
      .reduce((parentPath, relPath) => {
        const fullPath = concatPath(parentPath, relPath)
        const selfPath = concatSelfPath(fullPath)
        if(!_.has(registeredReducers, selfPath))
          _.setWith(registeredReducers, selfPath, s => s, Object)
        return fullPath
      }, '')
    */
    
    // now that the reducer tree is setup, we can add our own reducer
    // overrides existing reducer
    const reducerToRegister = (state = initialState, action) => reducer(state, action)
    const reducerPath = concatSelfPath(storePathName)
    _.setWith(registeredReducers, reducerPath, reducerToRegister, Object)
    
    // now everything is set up properly and we can replace the reducers on the store
    store.replaceReducer((state = {}, action) => {
      const staticReducer = existingReducer || combinedReducer
      let nextState = staticReducer(state, action)
      let hasChanges = state !== nextState

      // updates the state with registered reducer attached
      nextState = getListOfPathsBFS(registeredReducers)
        .reduce((incompleteState, storePath) => {
          const reducerPath = concatSelfPath(storePath)
          const reducer = _.get(registeredReducers, reducerPath, (s = {}) => s)

          //if (reducer === undefined)
          //  throw new Error({ message: 'Invalid for no reducer to exist', reducers: registeredReducers })

          const currentStorePathState = _.get(incompleteState, storePath)
          const newStorePathState = reducer(currentStorePathState, action)
          hasChanges = hasChanges || currentStorePathState !== newStorePathState

          const nextIncompleteState = { ...incompleteState }
          _.setWith(nextIncompleteState, storePath, newStorePathState, Object)
          return nextIncompleteState
        }, { ...nextState })

      return hasChanges ? nextState : state
    })
  }
}

// cleans up path within the tree that are empty
export const deregister = (storePathName) => {
  if(_.has(reducers, storePathName)) {
    const paths = storePathName.split('.')
      .slice(0, -1)
      .reduce((result, relPath, index) => {
        const parentPath = (index > 0) ? result[index - 1] : ''
        const fullPath = concatPath(parentPath, relPath)
        return [...result, fullPath]
      }, []).reverse()
    
    // delete last node at path
    _.unset(reducers, storePathName)

    // clean up paths in hierarchy if reducers don't exist
    paths.forEach(path => {
      const node = _.get(reducers, path)
      if(node === undefined)
        throw new Error({ 
          message: 'Invalid return value undefined, no object in path can be empty', 
          reducers: reducers 
        })
      else if 
        // if object has no keys we can delete the node
        (Object.keys(node).length === 0) _.unset(reducers, path)
    })
  }
}
